import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Icon } from '@/components/Icon';
import { usePlayer } from '@/modules/player/providers/PlayerProvider';
import { usePlayerPlayContext } from '@/modules/player/providers/dispatch/usePlayerPlayContext';
import { usePlayerPlaying } from '@/modules/player/providers/dispatch/usePlayerPlaying';
import { usePlayerStartMedia } from '@/modules/player/providers/dispatch/usePlayerStartMedia';
import { useQueueList } from '@/modules/player/providers/dispatch/useQueueList';
import { Media } from '@/types';

export interface PlayPauseProps {
  className?: string;
  inFlow?: boolean;
}

export const PlayPause = ({ className = '', inFlow = false }: PlayPauseProps) => {
  const { formatMessage } = useIntl();
  const { handlePlayPause } = usePlayerPlaying();
  const { startMedia } = usePlayerStartMedia();
  const { playing, currentMedia, playContext } = usePlayer();
  const queryClient = useQueryClient();
  const { setQueueList } = useQueueList();
  const { setPlayContext } = usePlayerPlayContext();

  const [isMatchingContext, setIsMatchingContext] = useState(false);

  useEffect(() => {
    setIsMatchingContext(inFlow && playContext === 'flow');
  }, [inFlow, playContext]);

  const mediasInCurrentPage = queryClient.getQueryData<Array<Media>>([
    inFlow ? 'flow' : 'current-medias',
  ]);

  const handleClick = useCallback(() => {
    if (currentMedia?.UUID && isMatchingContext) {
      handlePlayPause();
      return;
    }
    if (!mediasInCurrentPage?.[0]) return;

    setPlayContext(inFlow === true ? 'flow' : 'regular');
    setQueueList(mediasInCurrentPage);
    startMedia({ media: mediasInCurrentPage?.[0], autoPlay: true });
  }, [
    currentMedia?.UUID,
    isMatchingContext,
    mediasInCurrentPage,
    setPlayContext,
    inFlow,
    setQueueList,
    startMedia,
    handlePlayPause,
  ]);

  return (
    <button
      className={`play-button ${className}`}
      onClick={handleClick}
      type="button"
      aria-label={formatMessage({
        id: `player.accessibility.${playing ? 'pause' : 'play'}`,
      })}
    >
      <Icon
        name={!isMatchingContext ? 'play' : playing ? 'pause' : 'play'}
        className={classNames({
          'text-brand-neutral-300': !currentMedia,
        })}
      />
    </button>
  );
};
